import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { websocketService } from '../services';
import { mergeBlindboxGiftList } from '../utils/blindboxStats';
import './BlindBoxOverlay.css';

// API 基础 URL
const API_BASE = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'development' ? 'http://localhost:5000/api' : '/api');

const overlayRangeUsesLiveEnd = (config) => {
  return config?.stats_time_range_type !== 'custom' || !config?.stats_custom_end_time;
};

/**
 * OBS 透明浏览器源页面
 * 路由: /live/blindbox/:accessKey
 * 
 * accessKey 可以是:
 * - access_token (32位十六进制字符串) - 推荐，隐藏房间号
 * - room_id (纯数字) - 旧版兼容
 * 
 * 无需登录，通过 WebSocket 实时接收更新
 * 透明背景，适合叠加在 OBS 上
 * 
 * 显示配置从数据库读取，通过网站配置页面修改
 */
function BlindBoxOverlay() {
  const { roomId: accessKey } = useParams();
  const [searchParams] = useSearchParams();
  
  // 仅预览模式从 URL 参数获取
  const isPreview = searchParams.get('preview') === '1';
  
  // 判断 accessKey 类型
  const isToken = accessKey && /^[a-f0-9]{32}$/i.test(accessKey);
  
  // 实际的房间号（从配置中获取）
  const [roomId, setRoomId] = useState(isToken ? null : accessKey);
  
  // 配置（从数据库加载）
  const [selectedBlindBox, setSelectedBlindBox] = useState(null);
  const [displayTransparent, setDisplayTransparent] = useState(true);
  const [displayShowTitle, setDisplayShowTitle] = useState(true);
  const [displaySize, setDisplaySize] = useState('medium');
  const [displayShowTotalCount, setDisplayShowTotalCount] = useState(false);
  const [displayShowProfit, setDisplayShowProfit] = useState(false);
  const [timeRangeConfig, setTimeRangeConfig] = useState({ stats_time_range_type: 'current' });

  // 统计数据
  const [stats, setStats] = useState([]);
  const [blindGiftPrice, setBlindGiftPrice] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // 设置透明背景（兼容不支持 :has() 选择器的 OBS 浏览器）
  useEffect(() => {
    if (!isPreview) {
      // 非预览模式：设置透明背景
      document.documentElement.style.background = 'transparent';
      document.body.style.background = 'transparent';
      document.body.style.minHeight = 'auto';
    }
    
    return () => {
      // 组件卸载时恢复默认样式
      document.documentElement.style.background = '';
      document.body.style.background = '';
      document.body.style.minHeight = '';
    };
  }, [isPreview]);
  
  // 直接调用 API（避免依赖 blindboxService）
  const fetchConfig = useCallback(async () => {
    const url = isToken 
      ? `${API_BASE}/blindbox/obs/${accessKey}/config`
      : `${API_BASE}/blindbox/config/${accessKey}`;
    
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error('获取配置失败');
    }
    return response.json();
  }, [accessKey, isToken]);
  
  const fetchStats = useCallback(async (targetRoomId, blindGiftName) => {
    const url = isToken 
      ? `${API_BASE}/blindbox/obs/${accessKey}/stats?blindGiftName=${encodeURIComponent(blindGiftName)}`
      : `${API_BASE}/blindbox/stats/${targetRoomId}?blindGiftName=${encodeURIComponent(blindGiftName)}`;
    
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error('获取统计失败');
    }
    return response.json();
  }, [accessKey, isToken]);
  
  const loadData = useCallback(async () => {
    if (!accessKey) return;

    try {
      setLoading(true);
      setError(null);

      // 加载配置（获取选中的盲盒 + 显示选项 + room_id）
      const config = await fetchConfig();

      // 如果是 token 访问，从配置中获取 room_id
      if (isToken && config.room_id) {
        setRoomId(config.room_id);
      }

      const boxName = config.selected_blind_gift;
      setSelectedBlindBox(boxName);

      // 设置显示选项
      setDisplayTransparent(config.display_transparent !== false);
      setDisplayShowTitle(config.display_show_title !== false);
      setDisplaySize(config.display_size || 'medium');
      setDisplayShowTotalCount(config.display_show_total_count === true);
      setDisplayShowProfit(config.display_show_profit === true);
      setTimeRangeConfig({
        stats_time_range_type: config.stats_time_range_type || 'current',
        stats_custom_end_time: config.stats_custom_end_time || null
      });

      // 加载统计数据
      if (boxName) {
        const targetRoomId = isToken ? config.room_id : accessKey;
        const statsData = await fetchStats(targetRoomId, boxName);
        const boxData = statsData[boxName] || { gifts: [], blind_gift_price: 0 };
        setStats(mergeBlindboxGiftList(boxData.gifts || []));
        setBlindGiftPrice(boxData.blind_gift_price || 0);
      }
    } catch (err) {
      console.error('加载数据失败:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [accessKey, isToken, fetchConfig, fetchStats]);

  // 加载配置和初始数据
  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const timer = setInterval(loadData, 5 * 60 * 1000);
    return () => clearInterval(timer);
  }, [loadData]);
  
  // WebSocket 连接和事件监听
  useEffect(() => {
    // 需要 roomId 来订阅 WebSocket 事件
    if (!roomId) return;
    
    // 订阅房间
    websocketService.subscribeRoom(roomId);
    
    // 监听配置更新（当配置页面修改时重新加载配置）
    const handleConfigUpdate = async (data) => {
      if (String(data.data?.room_id) !== String(roomId)) return;
      
      loadData();
    };
    
    // 监听盲盒更新
    const handleBlindboxUpdate = (data) => {
      const updateData = data.data;
      if (String(updateData.room_id) !== String(roomId)) return;
      
      // 只处理当前选中盲盒的更新
      if (selectedBlindBox && updateData.blind_gift_name !== selectedBlindBox) return;
      if (!overlayRangeUsesLiveEnd(timeRangeConfig)) return;
      
      setStats(prevStats => mergeBlindboxGiftList(prevStats, updateData));
    };
    
    const handleBlindboxCleared = (data) => {
      if (String(data.data?.room_id) !== String(roomId)) return;
      setStats([]);
    };
    
    websocketService.on('blindbox_config_update', handleConfigUpdate);
    websocketService.on('blindbox_update', handleBlindboxUpdate);
    websocketService.on('blindbox_cleared', handleBlindboxCleared);
    
    return () => {
      websocketService.off('blindbox_config_update', handleConfigUpdate);
      websocketService.off('blindbox_update', handleBlindboxUpdate);
      websocketService.off('blindbox_cleared', handleBlindboxCleared);
      websocketService.unsubscribeRoom(roomId);
    };
  }, [roomId, selectedBlindBox, loadData, timeRangeConfig]);
  
  // 生成 className（使用数据库配置）
  const overlayClass = [
    'blindbox-overlay',
    isPreview ? 'preview-mode' : '',
    !displayTransparent ? 'with-background' : '',
    `size-${displaySize}`
  ].filter(Boolean).join(' ');
  
  if (loading) {
    return (
      <div className={overlayClass}>
        <div className="loading">加载中...</div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className={overlayClass}>
        <div className="error-hint">无效的链接</div>
      </div>
    );
  }
  
  if (!selectedBlindBox) {
    return (
      <div className={overlayClass}>
        <div className="no-config">未配置盲盒</div>
      </div>
    );
  }

  // 计算总数和盈亏
  // 数据库中 gift_price 和 blind_gift_price 单位都是金瓜子
  // 换算：100 金瓜子 = 1 电池
  const totalCount = stats.reduce((sum, gift) => sum + gift.count, 0);
  const totalValue = stats.reduce((sum, gift) => sum + (gift.gift_price / 100) * gift.count, 0); // 金瓜子转电池
  const totalCost = totalCount * (blindGiftPrice / 100); // 金瓜子转电池
  const profit = totalValue - totalCost;

  return (
    <div className={overlayClass}>
      {displayShowTitle && (
        <div className="overlay-title">{selectedBlindBox}</div>
      )}

      <div className="overlay-list">
        {stats.length > 0 ? (
          stats.map((gift, index) => (
            <div key={gift.gift_id || index} className="overlay-item">
              {gift.gift_img && (
                <img
                  src={gift.gift_img}
                  alt={gift.gift_name}
                  className="overlay-icon"
                  referrerPolicy="no-referrer"
                />
              )}
              <span className="overlay-name">{gift.gift_name}</span>
              <span className="overlay-count">x{gift.count}</span>
            </div>
          ))
        ) : (
          <div className="empty-hint">等待礼物...</div>
        )}
      </div>

      {/* 汇总信息 */}
      {(displayShowTotalCount || displayShowProfit) && stats.length > 0 && (
        <div className="overlay-summary">
          {displayShowTotalCount && (
            <div className="summary-item">
              <span className="summary-label">抽取总数</span>
              <span className="summary-value">{totalCount}</span>
            </div>
          )}
          {displayShowProfit && (
            <div className="summary-item">
              <span className="summary-label">盈亏</span>
              <span className={`summary-value ${profit >= 0 ? 'profit-positive' : 'profit-negative'}`}>
                {profit >= 0 ? '+' : ''}{profit}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default BlindBoxOverlay;
